import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import {
  BREAKPOINTS,
  MapService,
  RouteUtilsService,
} from '@lassi/webapp-shared';
import { map } from 'rxjs';

@Component({
  selector: 'lassi-embedded-map-page',
  imports: [MatSidenavModule, LeafletModule, RouterModule],
  template: `
    @if (hasNavbar()) {
      <ng-content select="navbar" />
    }
    <mat-drawer-container class="main" autosize [hasBackdrop]="false">
      <mat-drawer
        #drawer
        [mode]="drawerMode() ?? DEFAULT_DRAWER_MODE"
        [opened]="drawerOpened()"
        [style.--mat-sidenav-container-width]="
          drawerSize() ?? defaultDrawerSize()
        "
      >
        @if (mapService.mapSignal()) {
          <router-outlet #outlet="outlet" [name]="OUTLET_NAME" />
        }
      </mat-drawer>
      <mat-drawer-content>
        <ng-content></ng-content>
      </mat-drawer-content>
    </mat-drawer-container>
  `,
  styles: `
    @use 'breakpoints' as *;

    :host {
      height: 100svh;
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 1fr;
      grid-template-areas: 'main';
    }

    :host(.has-navbar) {
      --nav-size: 80px;
      grid-template-rows: 1fr;
      grid-template-columns: var(--nav-size) 1fr;
      grid-template-areas: 'nav main' 'nav main';
    }

    mat-drawer {
      box-shadow: var(--mat-app-elevation-shadow-level-4, none);
    }

    lassi-nav {
      grid-area: nav;
    }

    .main {
      grid-area: main;
    }
  `,
  host: {
    '[class.has-navbar]': 'hasNavbar()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmbeddedMapPageComponent {
  private readonly routeUtils = inject(RouteUtilsService);
  readonly mapService = inject(MapService);

  readonly OUTLET_NAME = RouteUtilsService.MAP_MENU_OUTLET_NAME;

  isWeb = inject(BREAKPOINTS).isWeb;

  readonly DEFAULT_DRAWER_MODE: MatDrawerMode = 'side';

  defaultDrawerSize = input('300px');
  hasNavbar = input(true);

  isOpen = this.routeUtils.isDrawerActive;
  isLanding = this.routeUtils.isBottomSheetLandingPage;
  drawerOpened = computed(() => this.isOpen() && !this.isLanding());
  drawerSize = toSignal(
    this.routeUtils.leafRouteData$.pipe(map((data) => data.drawerSize)),
  );
  drawerMode = toSignal(
    this.routeUtils.leafRouteData$.pipe(map((data) => data.drawerMode)),
  );
}
